import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  HomePageLayoutBlueprint,
  HomePageWidgetBlueprint,
} from '@backstage/plugin-home-react/alpha';
import type { JSX } from 'react';

type WidgetContent = (props?: unknown) => JSX.Element;

/**
 * Pass-through renderer so each widget renders our Backstage UI card directly
 * instead of being wrapped in the default Material UI `InfoCard`. This keeps the
 * whole home page on the OBOS Backstage UI design system.
 */
const renderBareCard = ({
  Content,
}: {
  Content: WidgetContent;
}): JSX.Element => <Content />;

/**
 * Custom Backstage UI layout that arranges the installed widgets. Registered on
 * the `page:home` extension provided by `@backstage/plugin-home`.
 */
const homePageLayout = HomePageLayoutBlueprint.make({
  params: {
    loader: () =>
      import('./components/HomePageLayout').then(m => m.HomePageLayout),
  },
});

/**
 * Helper to register an OBOS home page widget. The widget content renders a full
 * Backstage UI card; `renderBareCard` suppresses the default Material UI wrapper.
 */
function homeWidget(name: string, load: () => Promise<WidgetContent>) {
  return HomePageWidgetBlueprint.make({
    name,
    params: {
      name,
      components: () => load().then(Content => ({ Content })),
      componentProps: { Renderer: renderBareCard },
    },
  });
}

const githubAlertsWidget = homeWidget('github-alerts', () =>
  import('./components/HomeWidgets').then(m => m.GithubAlertsContent),
);

const blueprintAlertsWidget = homeWidget('blueprint-alerts', () =>
  import('./components/HomeWidgets').then(m => m.BlueprintAlertsContent),
);

const scorecardsWidget = homeWidget('scorecards', () =>
  import('./components/HomeWidgets').then(m => m.ScorecardsContent),
);

const announcementsWidget = homeWidget('announcements', () =>
  import('./components/HomeWidgets').then(m => m.AnnouncementsContent),
);

const myComponentsWidget = homeWidget('my-components', () =>
  import('./components/HomeWidgets').then(m => m.MyComponentsContent),
);

const docsUpdatesWidget = homeWidget('docs-updates', () =>
  import('./components/HomeWidgets').then(m => m.DocsUpdatesContent),
);

const favoritesWidget = homeWidget('favorites', () =>
  import('./components/HomeWidgets').then(m => m.FavoritesContent),
);

const lastVisitedWidget = homeWidget('last-visited', () =>
  import('./components/HomeWidgets').then(m => m.LastVisitedContent),
);

/**
 * Blueprint home module. Provides the custom OBOS layout and widgets for the
 * `home` plugin from `@backstage/plugin-home`, keeping all customization inside
 * this plugin so the core app stays close to upstream.
 */
export const homeModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    homePageLayout,
    githubAlertsWidget,
    blueprintAlertsWidget,
    scorecardsWidget,
    announcementsWidget,
    myComponentsWidget,
    docsUpdatesWidget,
    favoritesWidget,
    lastVisitedWidget,
  ],
});
